import { CampaignStatus } from '@atlas/types'
import { z } from 'zod'

/** A classification dimension selection: axis code → selected value codes. */
const campaignClassificationSchema = z.record(z.string(), z.array(z.string()))

/**
 * Create a Campaign (HTTP POST /campaigns body). `campaignCode`, `campaignId`,
 * `tenantId`, `status`, `isActive`, `createdBy`, and every timestamp are
 * server-owned — `.strict()` rejects them (422) rather than silently
 * stripping them.
 */
export const campaignCreateSchema = z
	.object({
		name: z.string().min(1),
		description: z.string().nullable().optional(),
		windowLabel: z.string().nullable().optional(),
		classification: campaignClassificationSchema.optional(),
		veevaCode: z.string().min(1).optional(),
	})
	.strict()

/**
 * Update a Campaign (HTTP PATCH /campaigns/{id} body). Every field is
 * optional, but at least one must be present. Omitting `description` or
 * `windowLabel` preserves the current value; an explicit `null` clears it —
 * `.nullable().optional()` is what makes that distinction possible. `name`
 * and `veevaCode` are not nullable: the column is `NOT NULL` and clearing a
 * Veeva code is out of scope.
 */
export const campaignUpdateSchema = z
	.object({
		name: z.string().min(1),
		description: z.string().nullable(),
		windowLabel: z.string().nullable(),
		classification: campaignClassificationSchema,
		veevaCode: z.string().min(1),
	})
	.partial()
	.strict()
	.refine((data) => Object.keys(data).length > 0, {
		message: 'At least one field must be provided',
	})

/** Transition a Campaign's lifecycle status (HTTP POST /campaigns/{id}/transition body). */
export const campaignTransitionSchema = z
	.object({
		to: z.enum(CampaignStatus),
	})
	.strict()

/**
 * Associate Files with a Campaign (HTTP POST /campaigns/{id}/files body).
 * Duplicate ids are normalized to a unique set; no Campaign-code field is
 * accepted — the code is resolved server-side from the path Campaign.
 * `.max(100)` reuses the batch ceiling already established for a comparable
 * File-id array (`fileRebindRequestSchema.fileIds`) — no new limit invented.
 */
export const campaignFileAssociationSchema = z
	.object({
		fileIds: z
			.array(z.uuid())
			.min(1)
			.max(100)
			.transform((ids) => [...new Set(ids)]),
	})
	.strict()

/**
 * Associate Messages with a Campaign (HTTP POST /campaigns/{id}/messages
 * body). Duplicate ids are normalized to a unique set; no Campaign-code
 * field is accepted — the code is resolved server-side from the path
 * Campaign. `.max(100)` reuses the same batch ceiling as `campaignFileAssociationSchema`.
 */
export const campaignMessageAssociationSchema = z
	.object({
		messageIds: z
			.array(z.uuid())
			.min(1)
			.max(100)
			.transform((ids) => [...new Set(ids)]),
	})
	.strict()

/**
 * Request a synchronous bulk-download ZIP for a Campaign's associated Files
 * (HTTP POST /campaigns/{id}/bulk-download body). `scope: 'selected'` carries
 * the caller's selection (deduplicated, order-preserving, capped at the MVP
 * 10-id limit — every id is re-validated server-side regardless); `scope:
 * 'all'` carries no id list, since the associated-File set is resolved
 * entirely server-side for that branch.
 */
export const campaignBulkDownloadSchema = z.discriminatedUnion('scope', [
	z
		.object({
			scope: z.literal('selected'),
			fileIds: z
				.array(z.uuid())
				.min(1)
				.max(10)
				.transform((ids) => [...new Set(ids)]),
		})
		.strict(),
	z
		.object({
			scope: z.literal('all'),
		})
		.strict(),
])

export type CampaignCreateInput = z.infer<typeof campaignCreateSchema>
export type CampaignUpdateInput = z.infer<typeof campaignUpdateSchema>
export type CampaignTransitionInput = z.infer<typeof campaignTransitionSchema>
export type CampaignFileAssociationInput = z.infer<typeof campaignFileAssociationSchema>
export type CampaignMessageAssociationInput = z.infer<typeof campaignMessageAssociationSchema>
export type CampaignBulkDownloadInput = z.infer<typeof campaignBulkDownloadSchema>
